import * as Sentry from '@sentry/react';
import { getApiBaseUrl, getGitRevision, getSentryDsn, getSentryEnvironment } from './config.ts';
import type { AppRouter } from './router.tsx';

// Initializes the browser Sentry SDK for the marketing site. Called from
// main.tsx before render. No-op when DSN is absent so local dev (and any
// deployed env without the secret set) stays unaffected.
//
// Marketing is the busiest ad-landing surface, so this is exactly where the
// ingest tunnel earns its keep: ad clicks arrive in LinkedIn/Facebook in-app
// WebViews and behind the content blockers that drop direct *.ingest.sentry.io
// requests. `tunnel` routes every envelope through our own API origin so that
// real-user telemetry keeps flowing (see api/src/routes/sentry-tunnel.ts).
export function initSentry(router: AppRouter): void {
  const dsn = getSentryDsn();
  if (!dsn) return;

  const apiBaseUrl = getApiBaseUrl();

  Sentry.init({
    dsn,
    environment: getSentryEnvironment(),
    release: getGitRevision(),
    // Drop noise from scripts our CSP correctly blocks. In-app browsers (the
    // LinkedIn / Facebook / Instagram WebViews users land in after tapping an
    // ad) inject their own tracking JavaScript into every page, and that
    // injected code calls `eval()`. Our `script-src` has no `unsafe-eval`, so
    // the browser throws an `EvalError` that bubbles to the global `onerror`
    // handler and gets reported here — even though nothing in our bundle is at
    // fault (we never call `eval`; such a bug would fail for everyone and break
    // in dev/E2E, not surface once from a single WebView). The CSP is doing
    // exactly its job; we just don't want an issue/alert each time it does.
    // Patterns cover the per-engine wording: Chromium (the EvalError thrown
    // here), Firefox, and WebKit/Safari. Each stays eval-specific — the
    // Chromium one anchors on `unsafe-eval`, the others on "eval()" /
    // "evaluate a string as JavaScript" — so we drop only eval noise and never
    // a different `script-src` violation (e.g. a real injected chunk).
    ignoreErrors: [
      /violates the following Content Security Policy directive.*unsafe-eval/i,
      /call to eval\(\) blocked by CSP/i,
      /Refused to evaluate a string as JavaScript/i,
    ],
    integrations: [
      // Route-aware browser tracing: names pageload/navigation transactions by
      // the matched TanStack route (e.g. `/pricing`, `/features/documents`,
      // source=route) rather than raw, high-cardinality URLs. Needs the router
      // instance, which is why the router is created at module scope in
      // router.tsx.
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      // Session Replay is added lazily after first paint — see
      // scheduleReplay() below. Keeping it out of the init integrations is
      // what lets it code-split into its own chunk.
    ],
    tracesSampleRate: 1.0,
    // Replay quota is the most likely budget pinch — 10% of normal sessions
    // and 100% of sessions with errors keeps coverage where it matters most.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Propagate trace headers to the API origin when the marketing site does
    // talk to it. Falls back to same-origin if the API URL hasn't been
    // configured (dev via the Vite proxy at /api).
    tracePropagationTargets: apiBaseUrl ? [apiBaseUrl] : [/^\/api/],
    // Tunnel events through our API so content blockers (Brave Shields,
    // EasyPrivacy lists used by uBlock Origin et al.) don't drop them on
    // the way to *.ingest.us.sentry.io. Only enabled when apiBaseUrl is
    // set: in dev without VITE_API_URL the API doesn't have an upstream
    // configured, so direct ingest is the right default there (and
    // ad-blockers are rarely the issue locally).
    tunnel: apiBaseUrl ? `${apiBaseUrl}/api/sentry-tunnel` : undefined,
    // Every collection category written out so the intent is explicit rather
    // than resting on SDK defaults. Everything is on: userInfo collects user
    // agent and IP (small and useful for debugging), and the marketing site
    // has no customer specification content to keep out.
    dataCollection: {
      userInfo: true,
      cookies: true,
      httpHeaders: { request: true, response: true },
      httpBodies: ['incomingRequest', 'outgoingRequest', 'incomingResponse', 'outgoingResponse'],
      urlQueryParams: true,
      genAI: { inputs: true, outputs: true },
    },
  });

  scheduleReplay();
}

// Attach Session Replay after the page is idle. Its chunk is the largest
// single dependency and isn't needed for first paint, so deferring its
// download + parse keeps it off the critical path. The trade-off is that a
// session's first ~idle-delay isn't replay-buffered; we attach as soon as the
// main thread is free (with a timeout ceiling) to keep that window small.
// Best-effort throughout: a failed load must never surface to the user.
function scheduleReplay(): void {
  const attach = (): void => {
    void import('./sentry-replay.ts')
      .then(({ buildReplayIntegration }) => {
        if (Sentry.getClient()) {
          Sentry.addIntegration(buildReplayIntegration());
        }
      })
      .catch(() => {
        // Replay is best-effort telemetry; swallow load/attach failures.
      });
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(attach, { timeout: 3000 });
  } else {
    setTimeout(attach, 2000);
  }
}
